import type { MediaTicketCache } from './media';
import type { ResolvedReference } from './references';

export interface VideoFrame { source: string; time: number; width: number; height: number }
type Capture = (url: string, time: number, signal: AbortSignal) => Promise<VideoFrame>;
type Subscriber = { resolve: (frame: VideoFrame) => void; reject: (error: unknown) => void; detach: () => void };
type Job = { key: string; reference: ResolvedReference; time: number; controller: AbortController; subscribers: Set<Subscriber> };
const cancelled = () => new DOMException('视频帧读取已取消', 'AbortError');

/** Short-lived decoders live in the trusted parent, where owned media can be
 * drawn to canvas. The opaque transcript receives raster bytes, never a decoder. */
export class VideoFrameCache {
  private frames = new Map<string, { frame: VideoFrame; expiresAt: number }>();
  private jobs = new Map<string, Job>();
  private queue: Job[] = [];
  private active = 0;
  private bytes = 0;
  private closed = false;
  constructor(private media: Pick<MediaTicketCache, 'resolve'>, private capture: Capture = captureVideoFrame,
    private now: () => number = Date.now) {}

  resolve(reference: ResolvedReference, time: number, signal?: AbortSignal): Promise<VideoFrame> {
    if (this.closed || signal?.aborted) return Promise.reject(cancelled());
    if (reference.kind !== 'video' || !Number.isFinite(time) || time < 0) return Promise.reject(new Error('视频时间无效'));
    const key = JSON.stringify([reference.target.fileId || reference.target.path, reference.contentRevision || reference.resourceUrl, time]);
    const cached = this.frames.get(key);
    if (cached && cached.expiresAt > this.now()) {
      this.frames.delete(key); this.frames.set(key, cached);
      return Promise.resolve(cached.frame);
    }
    if (cached) { this.bytes -= cached.frame.source.length; this.frames.delete(key); }
    let job = this.jobs.get(key);
    if (!job) {
      job = { key, reference, time, controller: new AbortController(), subscribers: new Set() };
      this.jobs.set(key, job); this.queue.push(job);
    }
    const current = job;
    const result = new Promise<VideoFrame>((resolve, reject) => {
      const abort = () => {
        current.subscribers.delete(subscriber); subscriber.detach(); reject(cancelled());
        if (!current.subscribers.size) {
          current.controller.abort();
          if (this.jobs.get(key) === current) this.jobs.delete(key);
        }
      };
      const subscriber: Subscriber = { resolve, reject, detach: () => signal?.removeEventListener('abort', abort) };
      current.subscribers.add(subscriber);
      signal?.addEventListener('abort', abort, { once: true });
    });
    this.pump(); return result;
  }

  private pump() {
    while (!this.closed && this.active < 2 && this.queue.length) {
      const job = this.queue.shift()!;
      if (job.controller.signal.aborted) continue;
      this.active++;
      void this.run(job).finally(() => { this.active--; this.pump(); });
    }
  }
  private async run(job: Job) {
    const signal = job.controller.signal;
    try {
      let media = await this.media.resolve(job.reference);
      signal.throwIfAborted();
      let frame: VideoFrame;
      try { frame = await this.capture(media.resourceUrl, job.time, signal); }
      catch (error) {
        if (signal.aborted) throw error;
        // A rejected/expired HTTP capability receives one fresh authenticated
        // ticket. This retry also releases the first decoder before continuing.
        media = await this.media.resolve(job.reference, true);
        signal.throwIfAborted();
        frame = await this.capture(media.resourceUrl, job.time, signal);
      }
      signal.throwIfAborted();
      this.frames.set(job.key, { frame, expiresAt: Math.min(media.expiresAt ?? Infinity, this.now() + 60_000) });
      this.bytes += frame.source.length;
      while (this.frames.size > 24 || this.bytes > 16_000_000) {
        const first = this.frames.keys().next().value!;
        this.bytes -= this.frames.get(first)!.frame.source.length; this.frames.delete(first);
      }
      for (const subscriber of job.subscribers) { subscriber.detach(); subscriber.resolve(frame); }
    } catch (error) {
      for (const subscriber of job.subscribers) { subscriber.detach(); subscriber.reject(error); }
    } finally {
      job.subscribers.clear();
      if (this.jobs.get(job.key) === job) this.jobs.delete(job.key);
    }
  }
  dispose() {
    this.closed = true;
    for (const job of this.jobs.values()) {
      job.controller.abort();
      for (const subscriber of job.subscribers) { subscriber.detach(); subscriber.reject(cancelled()); }
      job.subscribers.clear();
    }
    this.jobs.clear(); this.queue = []; this.frames.clear(); this.bytes = 0;
  }
}

export async function captureVideoFrame(url: string, time: number, signal: AbortSignal): Promise<VideoFrame> {
  const video = document.createElement('video');
  video.preload = 'auto'; video.muted = true; video.playsInline = true;
  const deadline = new AbortController();
  const abort = () => deadline.abort(signal.reason || cancelled());
  signal.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(() => deadline.abort(new Error('视频帧读取超时')), 12_000);
  const wait = (ready: () => boolean): Promise<void> => new Promise((resolve, reject) => {
    const local = new AbortController();
    const cleanup = () => { local.abort(); deadline.signal.removeEventListener('abort', failed); };
    const failed = () => { cleanup(); reject(deadline.signal.reason || new Error('视频帧读取失败')); };
    const check = () => {
      if (deadline.signal.aborted) { failed(); return; }
      if (video.error) { cleanup(); reject(new Error('视频媒体暂时不可用')); return; }
      if (ready()) { cleanup(); resolve(); }
    };
    deadline.signal.addEventListener('abort', failed, { once: true });
    for (const type of ['loadedmetadata', 'loadeddata', 'canplay', 'seeked', 'error']) video.addEventListener(type, check, { signal: local.signal });
    check();
  });
  try {
    signal.throwIfAborted(); video.src = url; video.load();
    await wait(() => video.readyState >= 1);
    if (!Number.isFinite(video.duration) || time > video.duration) throw new Error('所引时间超出视频长度');
    const target = Math.min(time, Math.max(0, video.duration - 0.001));
    video.currentTime = target;
    await wait(() => video.readyState >= 2 && !video.seeking && Math.abs(video.currentTime - target) < 0.1);
    deadline.signal.throwIfAborted();
    const scale = Math.min(1, 2048 / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale)); canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    const context = canvas.getContext('2d'); if (!context || !video.videoWidth) throw new Error('视频帧无法解码');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    let blob: Blob | null = null;
    for (const quality of [0.86, 0.7, 0.5]) {
      blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('视频帧无法编码')), 'image/jpeg', quality));
      deadline.signal.throwIfAborted();
      if (blob.size * 4 / 3 < 949_000) break;
    }
    if (!blob || blob.size * 4 / 3 >= 949_000) throw new Error('视频帧过大');
    const source = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(new Error('视频帧无法读取')); reader.readAsDataURL(blob!);
    });
    deadline.signal.throwIfAborted();
    return { source, time, width: canvas.width, height: canvas.height };
  } finally {
    clearTimeout(timer); signal.removeEventListener('abort', abort);
    video.pause(); video.removeAttribute('src'); video.load(); video.remove();
  }
}
